import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail, roleLabel } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PdfFolderSetup } from "@/components/PdfFolderSetup";

export default function MyAccount() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const first = new URLSearchParams(window.location.search).get("first") === "1";
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.next !== form.confirm) {
      setError("The two new passwords do not match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      setForm({ current: "", next: "", confirm: "" });
      await refreshUser();
      toast.success("Password changed — anyone else signed in as you is now signed out");
      if (first) navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fade-up" data-testid="my-account-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Your login</p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">My Account</h1>

      {first && (
        <div className="mt-4 max-w-lg border-l-2 border-[#C5A059] bg-[#FBF8F2] p-3 text-sm"
             data-testid="first-password-notice">
          Please choose your own password before you start. Enter the one-time password
          you were given as the current password.
        </div>
      )}

      <div className="mt-6 max-w-lg border border-border p-5">
        <dl className="grid grid-cols-2 gap-y-3 text-sm">
          <dt className="text-muted-foreground">Name</dt>
          <dd data-testid="account-name">{user?.name}</dd>
          <dt className="text-muted-foreground">Email</dt>
          <dd data-testid="account-email">{user?.email}</dd>
          <dt className="text-muted-foreground">Role</dt>
          <dd className="uppercase tracking-wider" data-testid="account-role">{roleLabel(user?.role)}</dd>
          <dt className="text-muted-foreground">Store</dt>
          <dd data-testid="account-branch">
            {["admin", "it_admin"].includes(user?.role) ? "Both stores" : user?.branch || "—"}
          </dd>
        </dl>
      </div>

      <form onSubmit={submit} className="mt-6 max-w-lg space-y-4 border border-border p-5">
        <h2 className="text-base font-semibold uppercase tracking-wider md:text-lg">
          Change your password
        </h2>
        <div>
          <Label className="text-xs uppercase tracking-wider">Current password</Label>
          <Input
            type="password"
            className="mt-1 h-10 rounded-none"
            value={form.current}
            onChange={(e) => setForm({ ...form, current: e.target.value })}
            required
            data-testid="current-password-input"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">New password</Label>
          <Input
            type="password"
            className="mt-1 h-10 rounded-none"
            value={form.next}
            onChange={(e) => setForm({ ...form, next: e.target.value })}
            required
            data-testid="account-new-password-input"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            At least 10 characters, with a letter and a number.
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Type it again</Label>
          <Input
            type="password"
            className="mt-1 h-10 rounded-none"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            required
            data-testid="account-confirm-password-input"
          />
        </div>
        {error && (
          <p className="text-sm font-semibold text-red-700" data-testid="account-error">
            {error}
          </p>
        )}
        <Button
          type="submit"
          disabled={busy}
          className="h-10 rounded-none bg-[#C5A059] font-semibold text-[#1b1b1b] hover:bg-[#B08F4A]"
          data-testid="change-password-button"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <KeyRound className="mr-2 h-4 w-4" />}
          Change password
        </Button>
      </form>

      <PdfFolderSetup />
    </div>
  );
}
